/**
 * User Table Component
 * Displays users with actions for approval/revoke and impersonation (View As)
 */

import { useState } from 'react';
import { CheckCircle, XCircle, RefreshCw, Search, Eye } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { Badge } from '../ui/badge';
import { useImpersonationStore } from '../../stores/impersonationStore';
import { useAuth } from '../../hooks/useAuth';
import type { User } from '../../routes/admin/users';

interface UserTableProps {
  users: User[];
  initialSearch?: string;
  onApprove: (email: string) => Promise<void>;
  onRevoke: (email: string) => Promise<void>;
  onRefresh: () => Promise<void>;
}

export function UserTable({ users, initialSearch, onApprove, onRevoke, onRefresh }: UserTableProps) {
  const [searchTerm, setSearchTerm] = useState(initialSearch ?? '');
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const { user: currentUser } = useAuth();
  const { startImpersonation, isImpersonating } = useImpersonationStore();

  // Filter users based on search
  const filteredUsers = users.filter(
    (user) =>
      user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.displayName?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleApprove = async (email: string) => {
    setActionLoading(email);
    try {
      await onApprove(email);
    } finally {
      setActionLoading(null);
    }
  };

  const handleRevoke = async (email: string) => {
    setActionLoading(email);
    try {
      await onRevoke(email);
    } finally {
      setActionLoading(null);
    }
  };

  const handleViewAs = (targetUser: User) => {
    if (!currentUser) {
      console.error('No current user found');
      return;
    }

    // Don't allow impersonating self
    if (targetUser.email.toLowerCase() === currentUser.email?.toLowerCase()) {
      console.warn('Cannot impersonate yourself');
      return;
    }

    // Don't allow impersonating other admins
    if (targetUser.role === 'ADMIN' || targetUser.role === 'SUPER_ADMIN') {
      console.warn('Cannot impersonate other administrators');
      return;
    }

    // Start impersonation
    startImpersonation(
      {
        id: currentUser.uid, // Firebase UID - will be resolved server-side
        email: currentUser.email || '',
        name: currentUser.displayName || currentUser.email || 'Admin'
      },
      {
        id: targetUser.id,
        email: targetUser.email,
        displayName: targetUser.displayName
      }
    );

    // Navigate to home page to view as the impersonated user
    // Use window.location to ensure full page reload with new context
    window.location.href = '/';
  };

  // Check if a user can be impersonated
  const canImpersonate = (user: User): boolean => {
    // Can't impersonate if already impersonating
    if (isImpersonating) return false;
    // Can't impersonate self
    if (user.email.toLowerCase() === currentUser?.email?.toLowerCase()) return false;
    // Can't impersonate admins
    if (user.role === 'ADMIN' || user.role === 'SUPER_ADMIN') return false;
    // Can only impersonate approved users (they have actual data to view)
    if (!user.approved) return false;
    return true;
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>Users ({filteredUsers.length})</CardTitle>
          <Button
            variant="outline"
            size="sm"
            onClick={onRefresh}
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {/* Search */}
        <div className="mb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by email or name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        {/* Table */}
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>User</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Joined</TableHead>
                <TableHead>Last Login</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredUsers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                    No users found
                  </TableCell>
                </TableRow>
              ) : (
                filteredUsers.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                          <span className="text-sm font-medium">
                            {user.email.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <span className="font-medium">
                          {user.displayName || 'N/A'}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>
                      <Badge
                        variant={user.approved ? 'default' : 'secondary'}
                        className={
                          user.approved
                            ? 'bg-green-100 text-green-700 hover:bg-green-100'
                            : 'bg-yellow-100 text-yellow-700 hover:bg-yellow-100'
                        }
                      >
                        {user.approved ? 'Approved' : 'Pending'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{user.role}</Badge>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {new Date(user.createdAt).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {user.lastLoginAt
                        ? new Date(user.lastLoginAt).toLocaleDateString()
                        : 'Never'}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        {/* View As button - only for approved non-admin users */}
                        {canImpersonate(user) && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleViewAs(user)}
                            title={`View platform as ${user.email}`}
                          >
                            <Eye className="h-4 w-4 mr-1" />
                            View As
                          </Button>
                        )}
                        {user.approved ? (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleRevoke(user.email)}
                            disabled={actionLoading === user.email}
                          >
                            <XCircle className="h-4 w-4 mr-1" />
                            Revoke
                          </Button>
                        ) : (
                          <Button
                            variant="default"
                            size="sm"
                            onClick={() => handleApprove(user.email)}
                            disabled={actionLoading === user.email}
                          >
                            <CheckCircle className="h-4 w-4 mr-1" />
                            Approve
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}
